import { useState, useRef } from 'react'
import { ClipboardList, Edit, Save } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import { Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const ResponsibilitiesSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [responsibilityOptions, setResponsibilityOptions] = useState([])
  const [isEditingResponsibilities, setIsEditingResponsibilities] = useState(false)
  const [editingResponsibilityIds, setEditingResponsibilityIds] = useState([])
  const [responsibilitySaving, setResponsibilitySaving] = useState(false)
  const [responsibilitiesLoading, setResponsibilitiesLoading] = useState(false)
  const responsibilitiesLoadedRef = useRef(false)

  const loadResponsibilityOptions = async () => {
    if (responsibilitiesLoadedRef.current) return responsibilityOptions
    responsibilitiesLoadedRef.current = true
    setResponsibilitiesLoading(true)
    try {
      const res = await libraries.getResponsibilities()
      const list = res?.data || res
      const normalized = Array.isArray(list) ? list : list?.data || []
      setResponsibilityOptions(normalized)
      return normalized
    } catch (err) {
      console.error('Error loading responsibilities:', err)
      responsibilitiesLoadedRef.current = false
      return []
    } finally {
      setResponsibilitiesLoading(false)
    }
  }

  const openResponsibilitiesEdit = () => {
    setIsEditingResponsibilities(true)
    loadResponsibilityOptions().then(options => {
      const validIds = new Set(options.map(r => r.id))
      setEditingResponsibilityIds([...new Set((talent.responsibilities || []).map(r => r.responsibility_id).filter(id => validIds.has(id)))])
    })
  }

  const handleResponsibilitiesSave = async () => {
    setResponsibilitySaving(true)
    try {
      const payload = [...new Set(editingResponsibilityIds)].map(rid => ({ responsibility_id: rid }))
      await talents.updateTalentResponsibilities(talentId, payload)
      const response = await talents.getTalentByIdWithRelations(talentId)
      if (response?.data) setTalent(response.data)
      setIsEditingResponsibilities(false)
      toast.success('Responsibilities saved', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      toast.error('Failed to save responsibilities', { duration: 2000, position: 'bottom-right' })
    } finally {
      setResponsibilitySaving(false)
    }
  }

  return (
    <SectionBlock id="responsibilities">
      <SectionHeader
        icon={ClipboardList}
        title="Responsibilities"
        action={!isEditingResponsibilities && hasPermission('talent-service', `${talent.talent_type}_responsibilities`, 'update') ? (
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
            onClick={openResponsibilitiesEdit}
            onMouseEnter={loadResponsibilityOptions}
            onFocus={loadResponsibilityOptions}
          >
            <Edit className="h-3.5 w-3.5" /> Edit
          </button>
        ) : null}
      />
      {isEditingResponsibilities ? (
        <div className="space-y-3">
          <Autocomplete
            multiple
            clearable
            fullWidth
            value={editingResponsibilityIds.map(String)}
            onChange={(vals) => setEditingResponsibilityIds((vals || []).map(Number))}
            options={responsibilityOptions.map(r => ({ value: String(r.id), label: r.name }))}
            placeholder={responsibilitiesLoading ? 'Loading…' : 'Select responsibilities...'}
            disabled={responsibilitySaving || responsibilitiesLoading}
          />
          <div className="flex gap-2">
            <button onClick={handleResponsibilitiesSave} disabled={responsibilitySaving || responsibilitiesLoading} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50">
              <Save className="h-3 w-3" /> Save
            </button>
            <button onClick={() => setIsEditingResponsibilities(false)} disabled={responsibilitySaving} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50">
              <XIcon className="h-3 w-3" /> Cancel
            </button>
          </div>
        </div>
      ) : talent.responsibilities?.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[...new Map((talent.responsibilities || []).map(r => [r.responsibility_id, r])).values()].map((responsibility) => (
            <div key={responsibility.responsibility_id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary flex items-start gap-3">
              <div className="w-7 h-7 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-800 shrink-0">
                <ClipboardList className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  {responsibility.responsibility?.name || `Responsibility #${responsibility.responsibility_id}`}
                </p>
                {responsibility.responsibility?.description && (
                  <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">{responsibility.responsibility.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No responsibilities specified</p>
      )}
    </SectionBlock>
  )
}

export default ResponsibilitiesSection
